import {
    Box,
    Collapse,
    Flex,
    HStack,
    Stack,
    useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useSelector } from 'react-redux';
import { Text } from '@/components/v4';

// Rendering thousands of diffs inside a modal is slow and unreadable.
const MAX_LISTED = 50;

const Value = ({ children, ...props }) => (
    <Text as="span" {...props}>
        {children || '—'}
    </Text>
);

/**
 * Collapsible "What will change" summary: counts in the header, and per
 * number the old value struck through next to the new one.
 */
const WhatWillChange = ({ changing, changingCount, unchangedCount }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { isOpen, onToggle } = useDisclosure();
    const borderColor = darkMode ? 'dark.tcap-borderGray' : 'gray.200';
    const hasChanges = changingCount > 0;
    const oldColor = darkMode ? 'red.300' : 'red.700';
    const newColor = darkMode ? 'green.300' : 'green.700';

    return (
        <Box border="1px solid" borderColor={borderColor} borderRadius="md">
            <Flex
                as="button"
                type="button"
                w="full"
                align="center"
                justify="space-between"
                px={3}
                py={2}
                bg={darkMode ? 'dark.tcap' : 'gray.50'}
                borderRadius="md"
                cursor={hasChanges ? 'pointer' : 'default'}
                aria-expanded={hasChanges ? isOpen : undefined}
                onClick={hasChanges ? onToggle : undefined}>
                <HStack spacing={1}>
                    {hasChanges &&
                        (isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />)}
                    <Text fontSize="sm" fontWeight="semibold">
                        What will change
                    </Text>
                </HStack>
                <Text fontSize="sm" color="gray.500">
                    {changingCount.toLocaleString()} changing ·{' '}
                    {unchangedCount.toLocaleString()} unchanged
                </Text>
            </Flex>

            <Collapse in={hasChanges && isOpen} animateOpacity>
                <Stack
                    spacing={3}
                    px={3}
                    py={2}
                    borderTop="1px solid"
                    borderColor={borderColor}>
                    {changing.slice(0, MAX_LISTED).map(({ row, diff }) => (
                        <Box key={row.id}>
                            <Text fontSize="sm" fontWeight="semibold">
                                {row.e164}
                            </Text>
                            {diff.map((change) => (
                                <Text key={change.field} fontSize="xs">
                                    <Text as="span" color="gray.500">
                                        {change.label}:{' '}
                                    </Text>
                                    <Value as="s" color={oldColor}>
                                        {change.from}
                                    </Value>
                                    {' → '}
                                    <Value
                                        color={newColor}
                                        fontWeight="semibold">
                                        {change.to}
                                    </Value>
                                </Text>
                            ))}
                        </Box>
                    ))}
                    {changing.length > MAX_LISTED && (
                        <Text fontSize="xs" color="gray.500">
                            …and{' '}
                            {(changing.length - MAX_LISTED).toLocaleString()}{' '}
                            more
                        </Text>
                    )}
                </Stack>
            </Collapse>
        </Box>
    );
};

export default WhatWillChange;
